/**
 * @fileoverview The metronome/tuner panel that opens INSIDE the recording
 * screen, so reaching for a tool no longer costs the instructor their lesson.
 *
 * Why a panel and not a link to /tools: `HansRecording` is a route, and leaving
 * it unmounts the page, whose cleanup stops the MediaRecorder and the mic
 * (`HansRecording.jsx`, the unmount effect). The footer used to `navigate()`
 * straight to /metronome mid-recording, silently ending the capture. Hosting
 * the same two components here keeps the recording alive; `ToolsPage` remains
 * the standalone host and is untouched.
 *
 * Two structural rules, both load-bearing:
 *
 * 1. **Nothing unmounts once opened.** `MetronomeTool` builds its AudioContext
 *    on mount and closes it on unmount, so unmounting the panel would stop the
 *    click and reset BPM every time it's dismissed. Open/closed and the tab
 *    switch are therefore CSS-only — `display: none` stops neither `setInterval`
 *    nor Web Audio, so the click keeps going while the panel is closed, which
 *    is what an instructor counting a student in actually wants. The first open
 *    is what mounts them, so an instructor who never touches the tools never
 *    pays for an AudioContext they didn't ask for.
 * 2. **The tuner borrows the capture stream** (see `TunerTool`'s header). It is
 *    handed the live recording stream rather than opening its own, because a
 *    second getUserMedia preempts the mic on iOS and would kill the lesson.
 *
 * The metronome's clicks reach the lesson audio acoustically through the mic —
 * accepted by design (the recording is transcription input, not a listening
 * copy). Headphones avoid it.
 */

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { SegmentedControl } from "@/components/studio/primitives";
import MetronomeTool from "@/components/studio/MetronomeTool";
import TunerTool from "@/components/studio/TunerTool";

const TABS = [
  { id: "metronome", label: "Metronome" },
  { id: "tuner", label: "Tuner" },
];

/**
 * @param {object} props
 * @param {boolean} props.open - Whether the panel is visible. Closed only hides
 *   it; the tools underneath keep running.
 * @param {"metronome"|"tuner"} props.tab - Active tab.
 * @param {(tab: string) => void} props.onTabChange
 * @param {() => void} props.onClose
 * @param {MediaStream} [props.stream] - The live capture, when recording, for
 *   the tuner to analyse instead of opening a second microphone.
 */
export default function RecordingTools({ open, tab, onTabChange, onClose, stream = null }) {
  // Latches on the first open and never returns to false — see rule 1. Before
  // that the panel is an empty invisible shell, so the tools cost nothing.
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  return (
    <div
      data-testid="recording-tools-panel"
      aria-hidden={!open}
      // Still mounted while closed, so keep its controls out of the tab order
      // and the a11y tree — `pointer-events-none` only stops the mouse.
      inert={!open || undefined}
      className={`fixed inset-x-0 bottom-0 top-0 z-40 flex justify-end transition-opacity duration-200 md:inset-y-0 md:left-auto ${
        open ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      {/* Scrim (mobile only — on desktop the panel sits beside the recorder) */}
      <button
        type="button"
        aria-label="Close practice tools"
        onClick={onClose}
        className="absolute inset-0 bg-[var(--studio-base)]/80 md:hidden"
      />

      <aside
        className={`relative flex h-full w-full flex-col border-l border-[var(--studio-divider)] bg-[var(--studio-raised)] shadow-2xl transition-transform duration-200 md:w-[420px] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between border-b border-[var(--studio-divider)] px-5 py-4">
          <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.25em] text-[var(--text-tertiary)]">
            Practice tools
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="close-recording-tools"
            aria-label="Close practice tools"
            className="rounded-full p-2 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)]"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 pb-10 pt-5">
          <SegmentedControl block value={tab} onChange={onTabChange} options={TABS} className="mb-8" />

          {/* Both stay mounted once opened — see rule 1 in the header.
              `hidden` keeps the inactive tool's audio and state alive while
              it's out of view. */}
          {mounted && (
            <>
              <div hidden={tab !== "metronome"}>
                <MetronomeTool />
              </div>
              <div hidden={tab !== "tuner"}>
                <TunerTool stream={stream} />
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
